// "Mark as automated" / "Not automated" on the ticket page.
//
// A flagged sender's mail goes to the FYI tab (no Slack card, no SLA clock). The
// flag lives on the contact (contacts_cache.is_automated), so it applies to every
// ticket from this sender, now and later.

import { Bot } from 'lucide-react';
import { useAuth } from '@/lib/auth-guard';
import { useAccess } from '@/lib/use-access';
import { useContact, useSetContactAutomated } from '@/lib/queries';
import { toastFailure, toastSuccess } from '@/lib/toast';

export function AutomatedSenderControl({
  senderEmail,
  robot,
}: {
  senderEmail: string | null | undefined;
  /** This ticket is currently robot mail (is_automated_sender from the server). */
  robot: boolean;
}) {
  const access = useAccess();
  const { teamMember } = useAuth();
  const email = senderEmail?.trim().toLowerCase() || undefined;
  const contactQ = useContact(email);
  const setAutomated = useSetContactAutomated();

  if (!email) return null;
  const flagged = contactQ.data?.is_automated === true;
  const canEdit = access.can('contacts.edit');
  if (!flagged && !canEdit) return null;

  const toggle = (automated: boolean) =>
    setAutomated.mutate(
      { email, automated, editorEmail: teamMember?.email ?? null },
      {
        onSuccess: () =>
          automated
            ? toastSuccess('Moved to FYI', 'Mail from this sender now waits in the FYI tab: no Slack card, no SLA clock.')
            : toastSuccess('No longer automated', 'Mail from this sender is handled like any other ticket again.'),
        onError: (e) => toastFailure(e, "Couldn't change that sender"),
      },
    );

  if (flagged) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-border/70 px-2 py-0.5 text-2xs text-muted-foreground"
        title="This sender is marked as automated, so its mail waits in the FYI tab with no Slack card and no SLA clock."
      >
        <Bot className="h-3 w-3 shrink-0" aria-hidden />
        <span>Automated sender{robot ? ' · no SLA clock' : ''}</span>
        {canEdit ? (
          <button
            type="button"
            disabled={setAutomated.isPending}
            onClick={() => toggle(false)}
            className="cursor-pointer font-medium text-foreground/80 underline-offset-2 hover:underline disabled:opacity-60"
          >
            Not automated
          </button>
        ) : null}
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={setAutomated.isPending}
      onClick={() => toggle(true)}
      className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-dashed border-border px-2 py-0.5 text-2xs text-muted-foreground transition-colors hover:border-border/80 hover:text-foreground disabled:opacity-60"
      title="Mail from this sender will go to the FYI tab: no Slack card, no SLA clock."
    >
      <Bot className="h-3 w-3 shrink-0" aria-hidden />
      Mark as automated
    </button>
  );
}
